// "Why": a hover (or keyboard focus) card that says where a number comes from, line by line. Every cost number on the
// run and project screens is wrapped in one, so nothing has to be taken on trust. Values lead, labels follow.
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "./controls.tsx";

export type WhyLine = { label: ReactNode; value?: ReactNode; detail?: ReactNode; strong?: boolean };

export function Why({
  title,
  lines,
  note,
  children,
  className,
  width = 360,
}: {
  title?: ReactNode;
  lines?: WhyLine[];
  note?: ReactNode;
  children: ReactNode;
  className?: string;
  width?: number;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const show = () => setRect(anchor.current?.getBoundingClientRect() ?? null);
  const hide = () => setRect(null);
  useLayoutEffect(() => {
    if (!rect) return setPos(null);
    const h = card.current?.offsetHeight ?? 0;
    const below = rect.bottom + 6;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, rect.top - h - 6) : below;
    setPos({ top, left: Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8) });
  }, [rect, width]);
  const empty = !title && !lines?.length && !note;
  return (
    <span
      ref={anchor}
      tabIndex={empty ? undefined : 0}
      onMouseEnter={empty ? undefined : show}
      onMouseLeave={hide}
      onFocus={empty ? undefined : show}
      onBlur={hide}
      className={cx(!empty && "cursor-help decoration-line-strong decoration-dotted underline-offset-[3px] hover:underline focus:outline-none", className)}
    >
      {children}
      {rect &&
        createPortal(
          <div
            ref={card}
            role="tooltip"
            style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
            className="pointer-events-none z-50 rounded-lg border border-line bg-surface px-3 py-2 text-left text-[12px] leading-snug text-text shadow-pop"
          >
            {title && <div className="mb-1 font-semibold">{title}</div>}
            {!!lines?.length && (
              <div className="flex flex-col gap-1">
                {lines.map((l, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list for one hover
                  <div key={i} className={cx(l.strong && "border-t border-line pt-1 font-semibold")}>
                    <div className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 text-muted">{l.label}</span>
                      {l.value != null && <span className="shrink-0 font-mono text-text tabular-nums">{l.value}</span>}
                    </div>
                    {l.detail && <div className="text-[11px] text-faint">{l.detail}</div>}
                  </div>
                ))}
              </div>
            )}
            {note && <div className={cx("text-[11px] text-muted", (title || lines?.length) && "mt-1.5")}>{note}</div>}
          </div>,
          document.body,
        )}
    </span>
  );
}
